import { useMemo, useCallback } from "react";

import { Condition, Masters, SearchAreaCategories } from "~/types";
import { filterByCategory } from "~/utils/categoryProcessing";

/**
 * 任意のエリアカテゴリに関する情報や処理をまとめた custom hook
 * 
 * TODO:categoryKeyを指定しない処理にする予定。
 * 
 * @param masters マスター情報（例：検索エリアの一覧）
 * @param searchAreaCategoriesMap 検索エリアのカテゴリマップ
 * @param filterByCategory カテゴリでエリアをフィルタリングする関数
 * @param categoryKey 操作対象のカテゴリ（例: "tokyo", "osaka"）
 * @returns エリア関連のデータと関数
 */
export function useSearchAreas(
  masters: Masters,
  searchAreaCategoriesMap: SearchAreaCategories,
  categoryKey: string
) {
  // 指定されたカテゴリのエリアのみを取得
  const areas = useMemo(() => {
    const category = searchAreaCategoriesMap[categoryKey];
    if (!category) return [];
    return filterByCategory(
      masters.searchAreas,
      category.id,
      "search_area_category_id"
    );
  }, [masters.searchAreas, searchAreaCategoriesMap, categoryKey]);

  // 条件により、特定のエリアカテゴリが全て選択されているか判定
  const isAreaSelected = useCallback(
    (conditions: Condition): boolean => {
      if (
        conditions.search_area_ids.length &&
        !conditions.occupation_type_ids.length &&
        !conditions.service_type_ids.length
      ) {
        return conditions.search_area_ids.every((id) =>
          areas.some((area) => area.id === id)
        );
      }
      return false;
    },
    [areas]
  );

  // エリアのSEOフレンドリーパスを生成する
  const generateAreaListPath = useCallback(
    (areaIds: string[]): string => {
      const category = searchAreaCategoriesMap[categoryKey];

      if (areaIds.length === 1) {
        const area = areas.find((area) => area.id === areaIds[0]);
        return `/${category.key}/${area?.key}`;
      }
      if (areaIds.length === areas.length) {
        return `/${category.key}`;
      }
      return "";
    },
    [areas, searchAreaCategoriesMap, categoryKey]
  );

  return { areas, isAreaSelected, generateAreaListPath };
}
